import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  Menu, X, Phone, Mail, MapPin, ArrowUpRight, ArrowRight,
  Calculator, FileText, Briefcase, TrendingUp, Award, Users,
  ClipboardList, ShieldCheck, Building2, Sparkles, CheckCircle2,
  Calendar, Quote, Star,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import CookieBanner from "@/components/CookieBanner";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import CookiePolicy from "@/components/CookiePolicy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV = [
  { label: "Studio", href: "#studio" },
  { label: "Servizi", href: "#servizi" },
  { label: "Clienti", href: "#testimonianze" },
  { label: "Contatti", href: "#contatti" },
];

const CALENDLY_URL = "https://calendly.com/consulenzamarano-info/30min";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-[#E5E5E0]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-4">
          <div className="h-14 w-14 lg:h-16 lg:w-16 rounded-full bg-[#111111] flex items-center justify-center flex-shrink-0">
            <img src="/assets/marano-mark.png" alt="Marano Consulting" className="h-[55%] w-auto object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-[26px] lg:text-[30px] text-[#111111]">Marano</div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#555]">Consulting</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className="text-sm text-[#111] hover:text-[#1B3022] transition-colors duration-200 relative group"
            >
              {n.label}
              <span className="absolute left-0 -bottom-1 w-0 h-px bg-[#1B3022] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>
        <a
          href="#contatti"
          data-testid="header-cta"
          className="hidden md:inline-flex items-center gap-2 bg-[#111111] text-white px-5 py-3 text-sm hover:bg-[#1B3022] transition-colors duration-300"
        >
          Contattaci <ArrowUpRight className="h-4 w-4" />
        </a>
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-[#111]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-[#E5E5E0]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className="text-base text-[#111]"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contatti"
              onClick={() => setOpen(false)}
              data-testid="mobile-header-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#111111] text-white px-5 py-3 text-sm"
            >
              Contattaci <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative pt-36 pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <div className="overline reveal" data-testid="hero-overline">Studio di Consulenza · Melito di Napoli</div>
          <h1
            data-testid="hero-title"
            className="font-serif-display text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] mt-6 text-[#111111] reveal reveal-delay-1"
          >
            Consulenza fiscale<br />
            <em className="not-italic text-[#1B3022]">e del lavoro</em><br />
            per imprese che<br />vogliono crescere.
          </h1>
          <p className="mt-8 max-w-xl text-[#555555] text-lg leading-relaxed reveal reveal-delay-2" data-testid="hero-subtitle">
            Marano Consulting affianca PMI, professionisti e startup con un approccio
            integrato: fisco, lavoro, finanza agevolata e accesso ai bandi sotto un unico studio.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 reveal reveal-delay-3">
            <a
              href="#contatti"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-[#1B3022] text-white px-7 py-4 text-sm hover:bg-[#2C4A36] transition-all duration-300 hover:-translate-y-0.5"
            >
              Richiedi una consulenza <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#servizi"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-[#111] text-[#111] px-7 py-4 text-sm hover:bg-[#111] hover:text-white transition-all duration-300"
            >
              Scopri i servizi
            </a>
          </div>
        </div>
        <div className="lg:col-span-5 reveal reveal-delay-2">
          <div className="relative">
            <div className="absolute -top-6 -left-6 right-6 bottom-6 border border-[#E5E5E0]" />
            <img
              src="https://images.pexels.com/photos/27292241/pexels-photo-27292241.jpeg"
              alt="Architettura moderna"
              className="relative w-full h-[460px] lg:h-[560px] object-cover grayscale"
              data-testid="hero-image"
            />
            <div className="absolute bottom-0 left-0 bg-white px-6 py-5 border border-[#E5E5E0]" data-testid="hero-stat">
              <div className="overline">Esperienza</div>
              <div className="font-serif-display text-3xl text-[#111] mt-1">10+ anni</div>
              <div className="text-xs text-[#555] mt-1">al fianco di aziende e professionisti</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ChiSiamo = () => (
  <section id="studio" data-testid="studio-section" className="py-24 lg:py-32 border-t border-[#E5E5E0]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="overline">Lo studio</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#111]" data-testid="studio-title">
            Due professionisti.<br />Una visione comune.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <p className="text-[#111] text-lg leading-relaxed" data-testid="studio-description">
            Marano Consulting nasce dall'unione delle competenze di due fratelli:
            <strong> Aniello Marano</strong>, Commercialista, e <strong>Orazio Marano</strong>,
            Consulente del Lavoro. Un team affiatato che mette al servizio dei propri
            clienti rigore tecnico, ascolto e una visione moderna della professione.
          </p>
          <p className="mt-6 text-[#555] leading-relaxed">
            Crediamo nella consulenza come relazione, non come servizio standardizzato.
            Ogni cliente ha le proprie esigenze: per questo costruiamo strategie su misura,
            con la riservatezza e la precisione che da sempre ci contraddistinguono.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-[#E5E5E0] mt-20 border border-[#E5E5E0]">
        {[
          {
            id: "nello",
            name: "Aniello Marano",
            role: "Dottore Commercialista",
            desc: "Specializzato in fiscalità d'impresa, contabilità e finanza agevolata. Affianca imprenditori e startup nei progetti di crescita e nell'accesso ai bandi.",
            icon: <Calculator className="h-6 w-6" />,
          },
          {
            id: "orazio",
            name: "Orazio Marano",
            role: "Consulente del Lavoro",
            desc: "Esperto in gestione del personale, elaborazione paghe, contrattualistica e relazioni sindacali. Punto di riferimento per aziende e professionisti.",
            icon: <Briefcase className="h-6 w-6" />,
          },
        ].map((p) => (
          <div
            key={p.id}
            data-testid={`partner-card-${p.id}`}
            className="bg-white p-10 lg:p-14 hover:bg-[#FBFBF9] transition-colors duration-300"
          >
            <div className="flex items-start justify-between">
              <div className="h-12 w-12 border border-[#1B3022] text-[#1B3022] flex items-center justify-center">
                {p.icon}
              </div>
              <div className="overline">{p.role}</div>
            </div>
            <h3 className="font-serif-display text-3xl mt-8 text-[#111]">{p.name}</h3>
            <p className="mt-4 text-[#555] leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Servizi = () => {
  const fiscale = [
    { icon: <FileText className="h-5 w-5" />, title: "Dichiarazione dei redditi", desc: "Modello Unico, 730, IVA, IRAP per privati, imprese e professionisti." },
    { icon: <ClipboardList className="h-5 w-5" />, title: "Contabilità ordinaria e semplificata", desc: "Tenuta libri contabili, bilanci, adempimenti periodici." },
    { icon: <Building2 className="h-5 w-5" />, title: "Apertura Partita IVA", desc: "Costituzione società, scelta regime fiscale, start-up." },
    { icon: <TrendingUp className="h-5 w-5" />, title: "Finanza agevolata", desc: "Crediti d'imposta, incentivi fiscali, agevolazioni per investimenti." },
    { icon: <Award className="h-5 w-5" />, title: "Accesso ai bandi", desc: "Bandi regionali, nazionali, PNRR ed europei: scouting, progettazione, rendicontazione." },
  ];
  const lavoro = [
    { icon: <FileText className="h-5 w-5" />, title: "Elaborazione buste paga", desc: "Cedolini, CU, 770, F24 con precisione e puntualità." },
    { icon: <ClipboardList className="h-5 w-5" />, title: "Contrattualistica", desc: "Assunzioni, cessazioni, trasformazioni, contratti di apprendistato e a termine." },
    { icon: <Users className="h-5 w-5" />, title: "Gestione del personale", desc: "Adempimenti INPS/INAIL, dimissioni telematiche, libro unico." },
    { icon: <ShieldCheck className="h-5 w-5" />, title: "Consulenza HR & sindacale", desc: "Politiche retributive, welfare aziendale, relazioni sindacali." },
  ];

  const Pillar = ({ overline, title, subtitle, items, side, testid }) => (
    <div data-testid={testid}>
      <div className="overline">{overline}</div>
      <h3 className="font-serif-display text-3xl md:text-4xl mt-3 text-[#111]">{title}</h3>
      <p className="text-[#555] mt-3 max-w-md">{subtitle}</p>
      <div className="mt-10 border-t border-[#E5E5E0]">
        {items.map((s, idx) => (
          <div
            key={idx}
            data-testid={`service-${side}-${idx}`}
            className="border-b border-[#E5E5E0] py-6 flex gap-5 items-start group hover:bg-white -mx-2 px-2 transition-colors"
          >
            <div className="text-[#1B3022] mt-1">{s.icon}</div>
            <div className="flex-1">
              <div className="text-[#111] font-medium text-[15px]">{s.title}</div>
              <div className="text-[#555] text-sm mt-1 leading-relaxed">{s.desc}</div>
            </div>
            <span className="text-[#555] text-xs font-mono mt-2">0{idx + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section id="servizi" data-testid="servizi-section" className="py-24 lg:py-32 bg-[#F0F0EB] border-t border-[#E5E5E0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-6">
            <div className="overline">I nostri servizi</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#111]" data-testid="servizi-title">
              Due aree integrate,<br />
              <em className="not-italic text-[#1B3022]">un unico interlocutore.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#111] text-lg leading-relaxed">
              Dalla gestione fiscale alle paghe, dall'accesso ai bandi alla consulenza HR:
              tutto sotto lo stesso tetto, con la garanzia di un dialogo costante tra le due aree.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
          <div className="bg-[#FBFBF9] p-10 lg:p-14">
            <Pillar
              testid="pillar-fiscale"
              overline="Area Fiscale & Aziendale · Aniello Marano"
              title="Commercialista"
              subtitle="Consulenza fiscale, contabile e strategica per imprese, professionisti e privati."
              items={fiscale}
              side="fiscale"
            />
          </div>
          <div className="bg-[#FBFBF9] p-10 lg:p-14">
            <Pillar
              testid="pillar-lavoro"
              overline="Area Lavoro & HR · Orazio Marano"
              title="Consulente del Lavoro"
              subtitle="Gestione del personale, paghe e relazioni industriali per realtà di ogni dimensione."
              items={lavoro}
              side="lavoro"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Valori = () => {
  const valori = [
    { n: "01", title: "Competenza", desc: "Aggiornamento continuo e specializzazione su normative fiscali, lavoro e finanza agevolata." },
    { n: "02", title: "Trasparenza", desc: "Preventivi chiari, comunicazione costante e nessuna sorpresa: il rapporto si fonda sulla fiducia." },
    { n: "03", title: "Vicinanza", desc: "Un team che ascolta, risponde rapidamente e affianca il cliente nelle decisioni più importanti." },
    { n: "04", title: "Modernità", desc: "Strumenti digitali, archivio condiviso e processi snelli per un servizio efficiente e su misura." },
  ];
  return (
    <section id="valori" data-testid="valori-section" className="py-24 lg:py-32 border-t border-[#E5E5E0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="overline">Perché sceglierci</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#111]" data-testid="valori-title">
              I valori che<br />guidano il nostro lavoro.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
          {valori.map((v) => (
            <div
              key={v.n}
              data-testid={`valore-${v.n}`}
              className="bg-[#FBFBF9] p-10 hover:bg-white transition-colors duration-300 group"
            >
              <div className="font-serif-display text-5xl text-[#1B3022] group-hover:text-[#111] transition-colors">{v.n}</div>
              <h3 className="font-serif-display text-2xl mt-6 text-[#111]">{v.title}</h3>
              <p className="text-[#555] mt-3 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-12 items-center">
          <img
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80"
            alt="Consulenza professionale"
            className="w-full h-[420px] object-cover grayscale"
            data-testid="valori-image"
          />
          <div>
            <div className="overline">Il nostro metodo</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-4 text-[#111]">
              Un approccio integrato che fa la differenza.
            </h3>
            <ul className="mt-8 space-y-4">
              {[
                "Analisi iniziale gratuita e senza impegno",
                "Strategia personalizzata fisco + lavoro",
                "Aggiornamenti periodici e reportistica chiara",
                "Supporto su finanza agevolata e bandi attivi",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#111]" data-testid={`metodo-item-${i}`}>
                  <CheckCircle2 className="h-5 w-5 text-[#1B3022] mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const Numeri = () => {
  const stats = [
    { value: "200+", label: "Clienti seguiti", desc: "tra area fiscale e lavoro" },
    { value: "1,5M+", label: "Bandi vinti", desc: "in finanziamenti ottenuti per i clienti" },
    { value: "98%", label: "Soddisfazione", desc: "clienti che confermano il mandato" },
    { value: "10+", label: "Anni di esperienza", desc: "al servizio di imprese e professionisti" },
  ];
  return (
    <section data-testid="numeri-section" className="py-24 lg:py-28 bg-[#1B3022] text-white border-t border-[#E5E5E0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="overline text-white/60">I nostri numeri</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-white" data-testid="numeri-title">
              Risultati concreti,<br />
              <em className="not-italic text-[#C8D8CE]">non parole.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-white/80 text-lg leading-relaxed">
              Oltre dieci anni di attività ci hanno permesso di costruire relazioni durature
              con imprenditori, professionisti e PMI della Campania e oltre.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`numero-${i}`}
              className="bg-[#1B3022] p-8 lg:p-10 hover:bg-[#2C4A36] transition-colors duration-500"
            >
              <div className="font-serif-display text-5xl lg:text-6xl text-white leading-none">{s.value}</div>
              <div className="overline text-[#C8D8CE] mt-5">{s.label}</div>
              <div className="text-white/70 text-sm mt-2 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonianze = () => {
  const items = [
    {
      id: "martina",
      initials: "MP",
      name: "Martina P.",
      role: "Titolare Studio di Architettura — Napoli",
      stars: 5,
      text: "Ho aperto la mia partita IVA con Marano Consulting e sono stata seguita passo dopo passo. Aniello mi ha guidato nella scelta del regime fiscale più conveniente, evitandomi errori costosi. Trovo finalmente uno studio che risponde nei tempi, in maniera chiara e sempre disponibile.",
    },
    {
      id: "marika",
      initials: "MV",
      name: "Marika V.",
      role: "Ristoratrice — Pozzuoli",
      stars: 5,
      text: "Grazie al lavoro di Orazio sulla gestione del personale, abbiamo riorganizzato i contratti dei nostri dipendenti ottimizzando i costi. Ci ha anche supportato durante un'ispezione e la sua preparazione ha fatto la differenza. Professionalità e cortesia ai massimi livelli.",
    },
    {
      id: "antonio",
      initials: "AE",
      name: "Antonio E.",
      role: "Titolare Call Center — Napoli",
      stars: 5,
      text: "Avviare un call center con tanti operatori non è semplice: serviva la scelta giusta del CCNL e l'individuazione delle agevolazioni contributive applicabili. Marano Consulting ci ha guidato passo passo, ottimizzando il costo del lavoro in totale conformità normativa. Un supporto strategico, non solo amministrativo.",
    },
    {
      id: "gennaro",
      initials: "GD",
      name: "Gennaro D.",
      role: "Founder Startup Tech — Napoli",
      stars: 5,
      text: "Cercavamo uno studio che capisse le startup e ci abbiamo trovato un partner. Marano Consulting ci ha seguiti dalla costituzione della SRL fino al primo bando PNRR vinto. Approccio moderno, niente burocratese, sempre dritti al punto.",
    },
    {
      id: "luigi",
      initials: "LG",
      name: "Luigi G.",
      role: "Azienda Commercio all'Ingrosso — Napoli",
      stars: 5,
      text: "Aniello e Orazio sono il nostro punto di riferimento da anni. Ci aggiornano costantemente sulle novità fiscali e del lavoro che ci riguardano. La cosa che apprezzo di più è la trasparenza: niente sorprese, sempre tutto chiaro fin dall'inizio.",
    },
  ];

  return (
    <section id="testimonianze" data-testid="testimonianze-section" className="py-24 lg:py-32 bg-[#FBFBF9] border-t border-[#E5E5E0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="overline">Dicono di noi</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#111]" data-testid="testimonianze-title">
              La fiducia<br />
              <em className="not-italic text-[#1B3022]">si costruisce nel tempo.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#111] text-lg leading-relaxed">
              Ecco cosa raccontano alcuni dei nostri clienti, che da anni hanno scelto
              Marano Consulting come partner per la crescita della loro attività.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
          {items.map((t) => (
            <article
              key={t.id}
              data-testid={`testimonial-${t.id}`}
              className="bg-white p-8 lg:p-10 flex flex-col group hover:bg-[#FBFBF9] transition-colors duration-300"
            >
              <Quote className="h-7 w-7 text-[#1B3022] opacity-30" />
              <div className="flex gap-0.5 mt-4" aria-label={`Valutazione: ${t.stars} su 5 stelle`}>
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-[#1B3022] text-[#1B3022]" />
                ))}
              </div>
              <p className="text-[#111] text-[15px] leading-relaxed mt-5 flex-1">{t.text}</p>
              <div className="mt-6 pt-5 border-t border-[#E5E5E0] flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-[#1B3022] text-white flex items-center justify-center font-serif-display text-base flex-shrink-0">
                  {t.initials}
                </div>
                <div>
                  <div className="text-[#111] font-medium text-sm">{t.name}</div>
                  <div className="text-[#555] text-xs mt-0.5">{t.role}</div>
                </div>
              </div>
            </article>
          ))}
          <a
            href="#contatti"
            data-testid="testimonianze-cta-card"
            className="bg-[#1B3022] text-white p-8 lg:p-10 flex flex-col justify-between group hover:bg-[#2C4A36] transition-colors duration-300"
          >
            <div>
              <div className="overline text-[#C8D8CE]">Il tuo prossimo passo</div>
              <h3 className="font-serif-display text-2xl lg:text-3xl mt-4 text-white leading-tight">
                Vuoi essere<br />il prossimo<br />nostro cliente?
              </h3>
              <p className="text-white/70 text-sm mt-4 leading-relaxed">
                Prenota una consulenza gratuita o scrivici una richiesta.
                Ti rispondiamo entro 24 ore lavorative.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-sm mt-6 group-hover:translate-x-1 transition-transform">
              Contattaci ora <ArrowRight className="h-4 w-4" />
            </div>
          </a>
        </div>

        <div className="mt-12 hidden text-center">
          <a href="#contatti" className="inline-flex items-center gap-2 text-[#1B3022] text-sm">
            Diventa anche tu un nostro cliente <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

const Contatti = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "", area: "Generale",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Richiesta inviata!", {
        description: res.data.email_sent
          ? "Ti risponderemo via email al più presto."
          : "Abbiamo ricevuto la tua richiesta. Ti contatteremo presto.",
      });
      setForm({ name: "", email: "", phone: "", subject: "", message: "", area: "Generale" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore nell'invio. Riprova tra qualche minuto.";
      toast.error("Invio non riuscito", { description: typeof msg === "string" ? msg : "Riprova." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#F0F0EB] border-0 border-b border-[#E5E5E0] px-4 py-4 text-[#111] placeholder-[#888] focus:outline-none focus:border-[#1B3022] focus:bg-white transition-all text-[15px]";

  return (
    <section id="contatti" data-testid="contatti-section" className="py-24 lg:py-32 border-t border-[#E5E5E0] bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">

        {/* Calendly CTA banner */}
        <div className="mb-20 border border-[#E5E5E0] bg-[#FBFBF9] p-8 lg:p-12 grid lg:grid-cols-[1fr_auto] gap-8 items-center" data-testid="calendly-banner">
          <div className="flex items-start gap-5">
            <div className="hidden md:flex h-14 w-14 rounded-full bg-[#1B3022] text-white items-center justify-center flex-shrink-0">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <div className="overline text-[#1B3022]">Prenotazione online</div>
              <h3 className="font-serif-display text-2xl lg:text-3xl mt-2 text-[#111]">
                Prenota una consulenza gratuita di 30 minuti
              </h3>
              <p className="text-[#555] mt-2 text-[15px] leading-relaxed max-w-2xl">
                Scegli direttamente tu giorno e ora che ti fanno comodo.
                Ti chiameremo o videochiameremo per una prima valutazione, senza impegno.
              </p>
            </div>
          </div>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calendly-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#1B3022] text-white px-7 py-4 text-sm hover:bg-[#2C4A36] transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Apri il calendario
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="overline">Contatti</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#111]" data-testid="contatti-title">
              Parliamone.<br />
              <em className="not-italic text-[#1B3022]">Il primo confronto è gratuito.</em>
            </h2>
            <p className="text-[#555] mt-6 text-lg leading-relaxed">
              Compila il modulo o scrivici direttamente. Ti risponderemo entro 24 ore
              lavorative con una prima valutazione e i possibili passi successivi.
            </p>

            <div className="mt-12 space-y-6">
              <a href="tel:+390818366932" data-testid="contact-phone" className="flex items-start gap-4 group">
                <div className="h-11 w-11 border border-[#E5E5E0] flex items-center justify-center text-[#1B3022] group-hover:bg-[#1B3022] group-hover:text-white transition-colors">
                  <Phone className="h-4 w-4" />
                </div>
                <div>
                  <div className="overline">Telefono</div>
                  <div className="text-[#111] mt-1 text-[15px]">081 1836 6932</div>
                </div>
              </a>
              <a href="mailto:info@consulenzamarano.com" data-testid="contact-email" className="flex items-start gap-4 group">
                <div className="h-11 w-11 border border-[#E5E5E0] flex items-center justify-center text-[#1B3022] group-hover:bg-[#1B3022] group-hover:text-white transition-colors">
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <div className="overline">Email</div>
                  <div className="text-[#111] mt-1 text-[15px]">info@consulenzamarano.com</div>
                </div>
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Via+Roma+544%2FA%2C+80017+Melito+di+Napoli+NA"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-address"
              >
                <div className="h-11 w-11 border border-[#E5E5E0] flex items-center justify-center text-[#1B3022] group-hover:bg-[#1B3022] group-hover:text-white transition-colors">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <div className="overline">Sede</div>
                  <div className="text-[#111] mt-1 text-[15px]">Via Roma, 544/A<br />80017 Melito di Napoli (NA)</div>
                  <div className="text-[11px] text-[#1B3022] mt-1 underline underline-offset-2">Apri in Google Maps</div>
                </div>
              </a>
            </div>

            {/* Google Maps embed */}
            <div className="mt-10 border border-[#E5E5E0] overflow-hidden" data-testid="contact-map">
              <iframe
                title="Sede Marano Consulting su Google Maps"
                src="https://www.google.com/maps?q=Via+Roma+544%2FA%2C+80017+Melito+di+Napoli+NA&output=embed"
                width="100%"
                height="280"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2" htmlFor="name">Nome e cognome *</label>
                  <input id="name" name="name" required minLength={2} value={form.name} onChange={onChange}
                    placeholder="Mario Rossi" className={inputCls} data-testid="contact-input-name" />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="email">Email *</label>
                  <input id="email" name="email" type="email" required value={form.email} onChange={onChange}
                    placeholder="email@esempio.it" className={inputCls} data-testid="contact-input-email" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2" htmlFor="phone">Telefono</label>
                  <input id="phone" name="phone" value={form.phone} onChange={onChange}
                    placeholder="+39 ..." className={inputCls} data-testid="contact-input-phone" />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="area">Area di interesse</label>
                  <select id="area" name="area" value={form.area} onChange={onChange}
                    className={inputCls} data-testid="contact-input-area">
                    <option value="Generale">Generale</option>
                    <option value="Commercialista">Area Fiscale (Commercialista)</option>
                    <option value="Lavoro">Area Lavoro (Consulente del Lavoro)</option>
                    <option value="Bandi">Finanza agevolata / Bandi</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-2" htmlFor="subject">Oggetto *</label>
                <input id="subject" name="subject" required minLength={2} value={form.subject} onChange={onChange}
                  placeholder="Es. Richiesta consulenza fiscale" className={inputCls} data-testid="contact-input-subject" />
              </div>
              <div>
                <label className="overline block mb-2" htmlFor="message">Messaggio *</label>
                <textarea id="message" name="message" required minLength={5} value={form.message} onChange={onChange}
                  placeholder="Raccontaci brevemente la tua esigenza..."
                  rows={6} className={`${inputCls} resize-none`} data-testid="contact-input-message" />
              </div>
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 bg-[#1B3022] text-white px-8 py-4 text-sm hover:bg-[#2C4A36] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Invio in corso..." : "Invia richiesta"}
                  {!submitting && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="text-xs text-[#555]">
                  I tuoi dati saranno trattati in conformità al GDPR e utilizzati esclusivamente per rispondere alla tua richiesta.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#111111] text-white pt-20 pb-10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 pb-16 border-b border-white/10">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-[#1a1a1a] ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <img src="/assets/marano-mark.png" alt="Marano Consulting" className="h-[55%] w-auto object-contain" />
            </div>
            <div>
              <div className="font-serif-display text-2xl lg:text-3xl">Marano Consulting</div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">Studio di consulenza</div>
            </div>
          </div>
          <p className="text-white/60 mt-6 max-w-md leading-relaxed">
            Consulenza fiscale e del lavoro per imprese, professionisti e startup.
            Una squadra di due fratelli, una visione integrata.
          </p>
        </div>
        <div className="lg:col-span-3">
          <div className="overline text-white/60">Studio</div>
          <ul className="mt-4 space-y-3 text-white/80 text-sm">
            <li><a href="#studio" className="hover:text-white">Chi siamo</a></li>
            <li><a href="#servizi" className="hover:text-white">Servizi</a></li>
            <li><a href="#valori" className="hover:text-white">Valori</a></li>
            <li><a href="#contatti" className="hover:text-white">Contatti</a></li>
          </ul>
        </div>
        <div className="lg:col-span-4">
          <div className="overline text-white/60">Contatti</div>
          <ul className="mt-4 space-y-3 text-white/80 text-sm">
            <li>Via Roma, 544/A — 80017 Melito di Napoli (NA)</li>
            <li>Tel: 081 1836 6932</li>
            <li>Email: info@consulenzamarano.com</li>
          </ul>
        </div>
      </div>
      <div className="pt-20 pb-6">
        <div className="font-serif-display text-[18vw] leading-none text-white/95 tracking-tighter">
          Marano.
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-white/10 text-xs text-white/50">
        <div>© {new Date().getFullYear()} Marano Consulting — Tutti i diritti riservati</div>
        <div className="flex gap-6">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white">Privacy</Link>
          <Link to="/cookie-policy" data-testid="footer-cookie-link" className="hover:text-white">Cookie</Link>
        </div>
      </div>
    </div>
  </footer>
);

const Home = () => {
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);
  return (
    <div className="App" data-testid="home-page">
      <Header />
      <main>
        <Hero />
        <ChiSiamo />
        <Numeri />
        <Servizi />
        <Valori />
        <Testimonianze />
        <Contatti />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      </Routes>
      <CookieBanner />
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: "#FBFBF9", border: "1px solid #E5E5E0", color: "#111111" },
        }}
      />
    </BrowserRouter>
  );
}

const PrivacyPolicyPage = () => (
  <div className="App">
    <Header />
    <PrivacyPolicy />
    <Footer />
  </div>
);

const CookiePolicyPage = () => (
  <div className="App">
    <Header />
    <CookiePolicy />
    <Footer />
  </div>
);

export default App;
